import {
  Button,
  ManageableItemsList,
  Modal,
  TTableRowAction,
} from '@payaca/ambrosia';
import { FC, useCallback, useMemo, useState, type JSX } from 'react';

import useArchiveTag from '@/api/mutations/tag/useArchiveTag';
import useUnarchiveTag from '@/api/mutations/tag/useUnarchiveTag';
import useGetMyPaginatedAvailableTags from '@/api/queries/me/useGetMyPaginatedAvailableTags';
import useGetMyRegionPreferences from '@/api/queries/me/useGetMyRegionPreferences';
import { GetTagsInput, PaginationInput, UpdateTagInput } from '@/gql/graphql';
import { CreateTagModal } from '@/ui/components/createTagModal/CreateTagModal';
import { EditTagModal } from '@/ui/components/editTagModal/EditTagModal';
import { PermissionGuard } from '@/ui/components/permissionGuard/PermissionGuard';

import Tag from '@payaca/components/tag/Tag';
import {
  DateFormats,
  getInternationalDateFormatByLocale,
} from '@payaca/helpers/internationalHelper';
import { AccountsPermissions } from '@payaca/permissions/accounts/accounts.permissions';
import { requestGetTags } from '@payaca/store/tags/tagsActions';
import { format as formatDate } from 'date-fns';
import { useDispatch } from 'react-redux';
import { Link } from 'react-router-dom';

const filterOptions = [
  {
    label: 'Active',
    value: 'active',
    disabled: true,
  },
  {
    label: 'Archived',
    value: 'archived',
  },
];

type InputState = {
  input: GetTagsInput;
  searchTerm?: string;
};

/** The Tags settings section, rendered in the Company Settings pane. */
export const TagsSettings: FC = (): JSX.Element => {
  const dispatch = useDispatch();

  const [inputState, setInputState] = useState<InputState>({
    input: {
      searchTerm: null,
      status: ['active'],
    },
    searchTerm: '',
  });

  const [pagination, setPagination] = useState<PaginationInput>({
    offset: 0,
    limit: 30,
  });

  const { data, isLoading, refetch } = useGetMyPaginatedAvailableTags(
    inputState.input,
    pagination
  );
  const { data: accountRegionPreferences } = useGetMyRegionPreferences();

  const [showCreateTagModal, setShowCreateTagModal] = useState(false);
  const [tagToEdit, setTagToEdit] = useState<UpdateTagInput>();
  const [tagIdToArchive, setTagIdToArchive] = useState<string>();

  const { mutateAsync: unarchiveTagMutation, isPending: isUnarchiving } =
    useUnarchiveTag();

  const itemActions = useMemo(() => {
    type ListedTagType = NonNullable<typeof data>['items'][0];

    const actions: TTableRowAction<ListedTagType>[] = [];

    if (
      inputState.input.status?.includes('active') ||
      !inputState.input.status?.length
    ) {
      actions.push(
        {
          label: 'Edit',
          onClick: (tag) => {
            setTagToEdit({
              tagId: tag.id,
              name: tag.name,
              colour: tag.colour,
            });
          },
          isDisabled: (tag) => !!tag.archivedAt,
        },
        {
          label: 'Archive',
          onClick: (tag) => {
            setTagIdToArchive(tag.id);
          },
          isDisabled: (tag) => !!tag.archivedAt,
        }
      );
    }

    if (inputState.input.status?.includes('archived')) {
      actions.push({
        label: 'Unarchive',
        isProcessing: isUnarchiving,
        onClick: (tag) => {
          // oxlint-disable-next-line typescript/no-floating-promises -- FIXME: pre-existing error: Promises must be awaited.
          unarchiveTagMutation({ tagId: tag.id }).then(() => {
            dispatch(requestGetTags());
            // oxlint-disable-next-line typescript/no-floating-promises -- FIXME: pre-existing error: Promises must be awaited.
            refetch();
          });
        },
        isDisabled: (tag) => !tag.archivedAt,
      });
    }

    return actions;
    // oxlint-disable-next-line react-hooks/exhaustive-deps -- FIXME: pre-existing error: React Hook useMemo has a missing dependency: 'dispatch'
  }, [inputState.input.status, isUnarchiving, unarchiveTagMutation, refetch]);

  return (
    <>
      <div className="p-4">
        <ManageableItemsList>
          <ManageableItemsList.HeaderBar
            buttons={
              <>
                <PermissionGuard
                  renderIfHasPermissions={[AccountsPermissions.MANAGE_TAGS]}
                >
                  <Button onClick={() => setShowCreateTagModal(true)}>
                    Create tag
                  </Button>
                </PermissionGuard>
              </>
            }
          />
          <ManageableItemsList.ActionBar>
            <ManageableItemsList.ActionBar.SearchInput
              value={inputState.searchTerm}
              onChange={(value) => {
                setInputState((x) => ({
                  ...x,
                  searchTerm: value,
                }));
              }}
              onChangeTimeout={() => {
                setInputState((x) => ({
                  ...x,
                  input: {
                    ...x.input,
                    searchTerm: x.searchTerm,
                  },
                }));
              }}
              onBlur={() => {
                setInputState((x) => ({
                  ...x,
                  input: {
                    ...x.input,
                    searchTerm: x.searchTerm,
                  },
                }));
              }}
              changeTimeoutMs={300}
            />
            <ManageableItemsList.ActionBar.BasicFilter
              options={filterOptions}
              value={inputState.input.status || []}
              onChange={(value) => {
                setInputState((x) => ({
                  ...x,
                  input: {
                    ...x.input,
                    status: value,
                  },
                }));
              }}
            />
          </ManageableItemsList.ActionBar>
          <ManageableItemsList.Table
            uniqueKey={'id'}
            items={data?.items || []}
            isLoading={isLoading}
            itemActions={itemActions}
          >
            <ManageableItemsList.Table.Column
              header=""
              field="name"
              render={(name, tag) => (
                <>
                  <Tag colour={tag.colour}>{tag.name}</Tag>
                </>
              )}
            />
            <ManageableItemsList.Table.Column
              header="Created"
              field="createdAt"
              render={(createdAt, tag) =>
                createdAt
                  ? formatDate(
                      createdAt,
                      getInternationalDateFormatByLocale(
                        DateFormats.SHORT,
                        accountRegionPreferences?.locale
                      )
                    )
                  : ''
              }
            />
            <ManageableItemsList.Table.Column
              className="text-right"
              header="Projects"
              field=""
              render={(_, tag) => {
                const initialRequestData = {
                  tagIds: [+tag.id],
                  pipelineId: null,
                  pipelineStages: [],
                  assignedUserIds: [],
                };

                return tag.usageSummary?.projectsCount ? (
                  <Link
                    to={`/deals?initialRequestData=${encodeURIComponent(
                      JSON.stringify(initialRequestData)
                    )}`}
                  >
                    {tag.usageSummary?.projectsCount}
                  </Link>
                ) : (
                  tag.usageSummary?.projectsCount
                );
              }}
            />
            <ManageableItemsList.Table.Column
              className="text-right"
              header="Events"
              field=""
              render={(_, tag) => tag.usageSummary?.eventsCount}
            />
            <ManageableItemsList.Table.Column
              className="text-right"
              header="Customers"
              field=""
              render={(_, tag) => tag.usageSummary?.customersCount}
            />
            {inputState.input.status?.includes('archived') && (
              <ManageableItemsList.Table.Column
                header="Archived"
                field="archivedAt"
                render={(archivedAt, tag) =>
                  archivedAt
                    ? formatDate(
                        archivedAt,
                        getInternationalDateFormatByLocale(
                          DateFormats.SHORT,
                          accountRegionPreferences?.locale
                        )
                      )
                    : ''
                }
              />
            )}
          </ManageableItemsList.Table>
          <ManageableItemsList.PaginationBar
            type="custom"
            pageSize={data?.limit || 30}
            currentPage={
              Math.ceil((data?.offset || 0) / (data?.limit || 30)) + 1
            }
            totalItems={data?.totalCount || 0}
            onPageChange={(page) =>
              setPagination((x) => ({
                ...x,
                offset: (page - 1) * (pagination?.limit || 30),
              }))
            }
          />
        </ManageableItemsList>
      </div>
      <CreateTagModal
        isOpen={showCreateTagModal}
        onClose={() => setShowCreateTagModal(false)}
        onSuccess={() => {
          dispatch(requestGetTags());
          // oxlint-disable-next-line typescript/no-floating-promises -- FIXME: pre-existing error: Promises must be awaited.
          refetch();
        }}
      />
      {tagToEdit && (
        <EditTagModal
          isOpen={!!tagToEdit}
          onClose={() => setTagToEdit(undefined)}
          tag={tagToEdit}
          onSuccess={() => {
            dispatch(requestGetTags());
            // oxlint-disable-next-line typescript/no-floating-promises -- FIXME: pre-existing error: Promises must be awaited.
            refetch();
            setTagToEdit(undefined);
          }}
        />
      )}
      {tagIdToArchive && (
        <ArchiveTagModal
          isOpen={!!tagIdToArchive}
          onClose={() => {
            setTagIdToArchive(undefined);
          }}
          tagId={tagIdToArchive}
          onSuccess={() => {
            dispatch(requestGetTags());
            // oxlint-disable-next-line typescript/no-floating-promises -- FIXME: pre-existing error: Promises must be awaited.
            refetch();
            setTagIdToArchive(undefined);
          }}
        />
      )}
    </>
  );
};

const ArchiveTagModal: FC<{
  isOpen: boolean;
  onClose: () => void;
  tagId: string;
  onSuccess: () => void;
}> = ({ isOpen, onClose, tagId, onSuccess }) => {
  const [isProcessing, setIsProcessing] = useState(false);
  const { mutateAsync: archiveTagMutation } = useArchiveTag();

  const onSubmit = useCallback(() => {
    setIsProcessing(true);
    // oxlint-disable-next-line typescript/no-floating-promises -- FIXME: pre-existing error: Promises must be awaited.
    archiveTagMutation({ tagId })
      .then(() => {
        onClose();
        onSuccess?.();
      })
      .finally(() => {
        setIsProcessing(false);
      });
  }, [onSuccess, archiveTagMutation, onClose, tagId]);
  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Archive tag">
      <Modal.Body>Are you sure you want to archive this tag?</Modal.Body>
      <Modal.Footer>
        <Modal.Footer.Actions>
          <Button
            colour="negative"
            isProcessing={isProcessing}
            onClick={() => !isProcessing && onSubmit()}
          >
            Yes, archive
          </Button>
        </Modal.Footer.Actions>
      </Modal.Footer>
    </Modal>
  );
};
